import { Injectable } from '@angular/core';
import { DbWorkerService } from '@app/core/db/db-worker.service';
import { skySwitchDB } from '@app/core/db/skyswitch-db';
import { estimateMemoryBytes, formatBytes, logHeapMemory } from '@app/core/db/utils/memory-logger';
import { Meeting } from '@shared/models/meeting.models';
import { BehaviorSubject, filter, map, Observable, of, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MeetingsStorageService {
  // ─── In-Memory Store ────────────────────────────────────────────────────────────
  // Primary source of truth for meetings after initial seed.
  // IDB becomes write-only (background persistence via worker).
  private readonly meetingsMap = new Map<string, Meeting>();
  private seeded = false;

  // Single notification subject — subscribers filter by ID, auto-cleaned on unsubscribe
  private readonly meetingChangedSubject = new Subject<{ id: string; value: Meeting | undefined }>();

  private readonly allMeetingsSubject = new BehaviorSubject<Meeting[]>([]);
  public readonly allMeetings$ = this.allMeetingsSubject.asObservable();

  constructor(private readonly dbWorker: DbWorkerService) {}

  // ─── Seed & Memory ──────────────────────────────────────────────────────────────

  /**
   * Populate the in-memory map from an array of meetings.
   * Called by MeetingsService after reading from IDB seed or API refresh.
   */
  public seedInMemory(meetings: Meeting[]): void {
    const oldIds = new Set(this.meetingsMap.keys());

    this.meetingsMap.clear();
    for (const meeting of meetings) {
      this.meetingsMap.set(meeting.id, meeting);
    }
    this.seeded = true;

    // Notify active observers with fresh data
    for (const [id, meeting] of this.meetingsMap) {
      this.notifyMeetingChange(id, meeting);
      oldIds.delete(id);
    }
    // Notify observers of items removed by re-seed
    for (const id of oldIds) {
      this.notifyMeetingChange(id, undefined);
    }

    this.emitAllMeetings();
    this.logMemoryUsage();
  }

  public logMemoryUsage(): void {
    const mapBytes = estimateMemoryBytes(Array.from(this.meetingsMap.values()));
    console.log(
      `[Memory][meetings] map: ${this.meetingsMap.size} entries, ` +
        `~${formatBytes(mapBytes)} (JSON lower-bound, real ~2-3x)`
    );
    logHeapMemory();
  }

  // ─── Read Methods (all in-memory) ───────────────────────────────────────────────

  public get(id: string): Meeting | undefined {
    return this.meetingsMap.get(id);
  }

  public getByMeetingId(meetingId: string): Meeting | undefined {
    for (const meeting of this.meetingsMap.values()) {
      if (meeting.meetingId === meetingId) {
        return meeting;
      }
    }
    return undefined;
  }

  public getLastItem(): Meeting | undefined {
    let earliest: Meeting | undefined;
    for (const meeting of this.meetingsMap.values()) {
      if (!earliest || meeting.createdDate < earliest.createdDate) {
        earliest = meeting;
      }
    }
    return earliest;
  }

  /**
   * Returns all meetings from the in-memory map if seeded, otherwise reads
   * from the unified worker's seedAll result (shares the single IDB read).
   */
  public workerGetAllMeetings(): Promise<Meeting[]> {
    if (this.seeded) {
      return Promise.resolve(Array.from(this.meetingsMap.values()));
    }

    return this.dbWorker.seeded.then((result) => result.meetings);
  }

  // ─── Observable Methods (notification-based) ────────────────────────────────────

  public observeMeeting(meetingId: string): Observable<Meeting | undefined> {
    if (!meetingId) {
      return of(undefined);
    }

    return new Observable<Meeting | undefined>((subscriber) => {
      const sub = this.meetingChangedSubject
        .pipe(
          filter((change) => change.id === meetingId),
          map((change) => change.value)
        )
        .subscribe(subscriber);
      subscriber.next(this.meetingsMap.get(meetingId));
      return sub;
    });
  }

  // ─── Write Methods (in-memory-first + fire-and-forget IDB) ─────────────────────

  public addMeetingItem(id: string, meeting: Meeting): void {
    this.meetingsMap.set(id, meeting);
    this.notifyMeetingChange(id, meeting);
    this.emitAllMeetings();

    // Fire-and-forget IDB persistence
    this.dbWorker.callWorker('bulkPut', { table: 'meetings', items: [meeting] });
  }

  public updateMeetingItem(id: string, fieldsToUpdate: Partial<Meeting>): void {
    const existing = this.meetingsMap.get(id);

    if (existing) {
      Object.assign(existing, fieldsToUpdate);
      this.notifyMeetingChange(id, existing);
      this.emitAllMeetings();

      // Fire-and-forget IDB persistence
      this.dbWorker.callWorker('bulkPut', { table: 'meetings', items: [existing] });
    }
  }

  public removeMeetingItem(id: string): void {
    this.meetingsMap.delete(id);
    this.notifyMeetingChange(id, undefined);
    this.emitAllMeetings();

    // Fire-and-forget IDB persistence
    this.dbWorker.callWorker('bulkDelete', { table: 'meetings', keys: [id] });
  }

  public replaceWith(items: Meeting[]): void {
    // Compute IDs to delete from IDB (old entries not in new list)
    const newIds = new Set(items.map((item) => item.id));
    const deletedIds: string[] = [];
    for (const id of this.meetingsMap.keys()) {
      if (!newIds.has(id)) {
        deletedIds.push(id);
      }
    }

    // Replace in-memory map
    this.meetingsMap.clear();
    for (const item of items) {
      this.meetingsMap.set(item.id, item);
    }

    // Notify observers of new/updated meetings and removed meetings
    for (const item of items) {
      this.notifyMeetingChange(item.id, item);
    }
    for (const id of deletedIds) {
      this.notifyMeetingChange(id, undefined);
    }

    this.emitAllMeetings();

    // Fire-and-forget IDB persistence
    if (items.length > 0) {
      this.dbWorker.callWorker('bulkPut', { table: 'meetings', items });
    }
    if (deletedIds.length > 0) {
      this.dbWorker.callWorker('bulkDelete', { table: 'meetings', keys: deletedIds });
    }
  }

  // ─── Drop / Reset ──────────────────────────────────────────────────────────────

  public async drop(): Promise<void> {
    const oldIds = Array.from(this.meetingsMap.keys());

    // Clear in-memory state
    this.meetingsMap.clear();
    this.seeded = false;

    // Notify active observers that their items are gone
    for (const id of oldIds) {
      this.notifyMeetingChange(id, undefined);
    }

    this.allMeetingsSubject.next([]);

    // Reset the database
    await skySwitchDB.resetMeetings();
  }

  // ─── Private Helpers ───────────────────────────────────────────────────────────

  private notifyMeetingChange(id: string, meeting: Meeting | undefined): void {
    this.meetingChangedSubject.next({ id, value: meeting });
  }

  private emitAllMeetings(): void {
    this.allMeetingsSubject.next(Array.from(this.meetingsMap.values()));
  }
}
